import api from "src/core/axios";
import APIResponse from "src/types/response.type";

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  status: 'Active' | 'Draft' | 'Archived';
  nodesStatus: 'Draft' | 'Published';
  currentNodes: any[];
  liveNodes: any[];
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowListResponse {
  workflows: Workflow[];
  pagination: {
    page: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
  };
}

export interface CreateWorkflowDto {
  name: string;
  description?: string;
}

export interface UpdateWorkflowDto {
  name?: string;
  description?: string;
  status?: string;
  nodesStatus?: string;
  currentNodes?: any[];
}

export interface WorkflowNodesDto {
  nodes: any[];
}

export interface TestScenario {
  id: string;
  name: string;
  description: string;
  input: any;
  expectedOutput: any;
  variables?: Record<string, any>;
}

export interface TestResult {
  id: string;
  scenarioId: string;
  workflowId: string;
  status: 'success' | 'failure' | 'error';
  input: any;
  output: any;
  expectedOutput: any;
  executionTime: number;
  createdAt: string;
}

export interface WorkflowTestSuite {
  id: string;
  name: string;
  description: string;
  workflowId: string;
  scenarios: TestScenario[];
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowStatistics {
  totalWorkflows: number;
  activeWorkflows: number;
  draftWorkflows: number;
  archivedWorkflows: number;
  publishedWorkflows: number;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  averageExecutionTime: number;
}

export interface WorkflowPerformance {
  workflowId?: string;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  averageExecutionTime: number;
  peakExecutionTime: number;
  throughput: number;
  errorRate: number;
}

export interface WorkflowAnalytics {
  executionTrends: {
    date: string;
    executions: number;
    successRate: number;
    averageTime: number;
  }[];
  topWorkflows: {
    workflowId: string;
    name: string;
    executionCount: number;
    successRate: number;
  }[];
  channelPerformance: {
    channelType: string;
    executionCount: number;
    successRate: number;
  }[];
}

export interface WorkflowVersion {
  id: string;
  version: number;
  changes: string;
  createdAt: string;
  createdBy: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  nodes: any[];
  variables: Record<string, any>;
  createdAt: string;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  status: string;
  input: any;
  output: any;
  startedAt: string;
  completedAt?: string;
  executionTime?: number;
  error?: string;
}

export interface WorkflowVariable {
  name: string;
  value: any;
  type: string;
  description?: string;
}

export interface WorkflowKeyword {
  keyword: string;
  action: string;
  description?: string;
}

// Workflow API calls
export const fetchWorkflows = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  nodesStatus?: string; // Add this line
} = {}): Promise<APIResponse<WorkflowListResponse>> => {
  try {
    const { page = 1, limit = 10, search, status, nodesStatus } = params; // Add nodesStatus here
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(status && { status }),
      ...(nodesStatus && { nodesStatus }) // Add this line
    });

    const response = await api.get(`/workflow?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflows:', error);
    throw error;
  }
};

export const getWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.get(`/workflow/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow:', error);
    throw error;
  }
};

export const getActiveWorkflowNames = async (): Promise<APIResponse<string[]>> => {
  try {
    const response = await api.get('/workflow/active-names');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch active workflow names:', error);
    throw error;
  }
};

export const createWorkflow = async (workflow: CreateWorkflowDto): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post('/workflow', workflow);
    return response.data;
  } catch (error) {
    console.error('Failed to create workflow:', error);
    throw error;
  }
};

export const updateWorkflow = async (id: string, workflow: UpdateWorkflowDto): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}`, workflow);
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow:', error);
    throw error;
  }
};

export const updateWorkflowStatus = async (id: string, status: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.put(`/workflow/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow status:', error);
    throw error;
  }
};

export const duplicateWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}/duplicate`);
    return response.data;
  } catch (error) {
    console.error('Failed to duplicate workflow:', error);
    throw error;
  }
};

export const deleteWorkflow = async (id: string): Promise<void> => {
  try {
    await api.delete(`/workflow/${id}`);
  } catch (error) {
    console.error('Failed to delete workflow:', error);
    throw error;
  }
};

export const publishWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}/publish`);
    return response.data.workflow;
  } catch (error) {
    console.error('Failed to publish workflow:', error);
    throw error;
  }
};

export const archiveWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}/archive`);
    return response.data.workflow;
  } catch (error) {
    console.error('Failed to archive workflow:', error);
    throw error;
  }
};

export const restoreWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}/restore`);
    return response.data.workflow;
  } catch (error) {
    console.error('Failed to restore workflow:', error);
    throw error;
  }
};

export const pauseWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}/pause`);
    return response.data.workflow;
  } catch (error) {
    console.error('Failed to pause workflow:', error);
    throw error;
  }
};

export const activateWorkflow = async (id: string): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/${id}/activate`);
    return response.data.workflow;
  } catch (error) {
    console.error('Failed to activate workflow:', error);
    throw error;
  }
};

export const importWorkflow = async (file: File): Promise<APIResponse<Workflow>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/workflow/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to import workflow:', error);
    throw error;
  }
};

export const exportWorkflow = async (id: string): Promise<Blob> => {
  try {
    const response = await api.post(`/workflow/${id}/export`, {}, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Failed to export workflow:', error);
    throw error;
  }
};

export const updateWorkflowNodes = async (
  id: string,
  nodes: any[],
  nodesStatus?: string,
  lastUpdatedAt?: string,
): Promise<{ updatedAt: string }> => {
  try {
    const response = await api.put(`/workflow/${id}/nodes`, {
      nodes,
      ...(nodesStatus && { nodesStatus }),
      ...(lastUpdatedAt && { lastUpdatedAt }),
    });
    return response.data?.data ?? { updatedAt: new Date().toISOString() };
  } catch (error) {
    console.error('Failed to update workflow nodes:', error);
    throw error;
  }
};

export const getWorkflowStatistics = async (params: {
  startDate?: string;
  endDate?: string;
} = {}): Promise<APIResponse<WorkflowStatistics>> => {
  try {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get(`/workflow/statistics?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow statistics:', error);
    throw error;
  }
};

export const getWorkflowPerformance = async (params: {
  workflowId?: string;
  startDate?: string;
  endDate?: string;
} = {}): Promise<APIResponse<WorkflowPerformance>> => {
  try {
    const { workflowId, startDate, endDate } = params;
    const queryParams = new URLSearchParams();

    if (workflowId) queryParams.append('workflowId', workflowId);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get(`/workflow/performance?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow performance:', error);
    throw error;
  }
};

export const getWorkflowAnalytics = async (params: {
  startDate?: string;
  endDate?: string;
} = {}): Promise<APIResponse<WorkflowAnalytics>> => {
  try {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get(`/workflow/analytics?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow analytics:', error);
    throw error;
  }
};

export const getWorkflowVersions = async (id: string): Promise<APIResponse<WorkflowVersion[]>> => {
  try {
    const response = await api.get(`/workflow/${id}/versions`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow versions:', error);
    throw error;
  }
};

export const validateWorkflow = async (id: string, nodes: any[]): Promise<APIResponse<any>> => {
  try {
    const response = await api.post(`/workflow/${id}/validate`, { nodes });
    return response.data;
  } catch (error) {
    console.error('Failed to validate workflow:', error);
    throw error;
  }
};

export const getWorkflowTemplates = async (): Promise<APIResponse<WorkflowTemplate[]>> => {
  try {
    const response = await api.get('/workflow/templates');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow templates:', error);
    throw error;
  }
};

export const createWorkflowFromTemplate = async (templateId: string, data: {
  name: string;
  description?: string;
}): Promise<APIResponse<Workflow>> => {
  try {
    const response = await api.post(`/workflow/templates/${templateId}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create workflow from template:', error);
    throw error;
  }
};

// Testing endpoints
export const createTestSuite = async (workflowId: string, data: {
  name: string;
  description: string;
  scenarios: TestScenario[];
}): Promise<APIResponse<WorkflowTestSuite>> => {
  try {
    const response = await api.post(`/workflow/${workflowId}/test-suite`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create test suite:', error);
    throw error;
  }
};

export const runTestScenario = async (workflowId: string, data: {
  scenario: TestScenario;
  variables?: Record<string, any>;
}): Promise<APIResponse<TestResult>> => {
  try {
    const response = await api.post(`/workflow/${workflowId}/test-scenario`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to run test scenario:', error);
    throw error;
  }
};

export const getTestHistory = async (workflowId: string, limit: number = 50): Promise<APIResponse<TestResult[]>> => {
  try {
    const response = await api.get(`/workflow/${workflowId}/test-history?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch test history:', error);
    throw error;
  }
};

// Workflow Execution endpoints
export const getWorkflowExecutions = async (params: {
  workflowId?: string;
  status?: string;
  page?: number;
  limit?: number;
} = {}): Promise<APIResponse<{
  executions: WorkflowExecution[];
  pagination: {
    page: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
  };
}>> => {
  try {
    const { workflowId, status, page = 1, limit = 10 } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(workflowId && { workflowId }),
      ...(status && { status })
    });

    const response = await api.get(`/workflow-execution/executions?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow executions:', error);
    throw error;
  }
};

export const getWorkflowExecution = async (id: string): Promise<APIResponse<WorkflowExecution>> => {
  try {
    const response = await api.get(`/workflow-execution/executions/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow execution:', error);
    throw error;
  }
};

export const getWorkflowExecutionLogs = async (id: string): Promise<APIResponse<any[]>> => {
  try {
    const response = await api.get(`/workflow-execution/executions/${id}/logs`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch execution logs:', error);
    throw error;
  }
};

export const deleteWorkflowExecution = async (id: string): Promise<void> => {
  try {
    await api.delete(`/workflow-execution/executions/${id}`);
  } catch (error) {
    console.error('Failed to delete execution:', error);
    throw error;
  }
};

// Variables management
export const getWorkflowVariables = async (workflowId: string): Promise<APIResponse<WorkflowVariable[]>> => {
  try {
    const response = await api.get(`/workflow-execution/workflows/${workflowId}/variables`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow variables:', error);
    throw error;
  }
};

export const updateWorkflowVariables = async (workflowId: string, variables: Record<string, any>): Promise<APIResponse<void>> => {
  try {
    const response = await api.put(`/workflow-execution/workflows/${workflowId}/variables`, variables);
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow variables:', error);
    throw error;
  }
};

export const addWorkflowVariable = async (workflowId: string, variable: {
  name: string;
  value: any;
  type: string;
}): Promise<APIResponse<void>> => {
  try {
    const response = await api.post(`/workflow-execution/workflows/${workflowId}/variables`, variable);
    return response.data;
  } catch (error) {
    console.error('Failed to add workflow variable:', error);
    throw error;
  }
};

export const deleteWorkflowVariable = async (workflowId: string, variableName: string): Promise<void> => {
  try {
    await api.delete(`/workflow-execution/workflows/${workflowId}/variables/${variableName}`);
  } catch (error) {
    console.error('Failed to delete workflow variable:', error);
    throw error;
  }
};

// Keywords management
export const getWorkflowKeywords = async (workflowId: string): Promise<APIResponse<WorkflowKeyword[]>> => {
  try {
    const response = await api.get(`/workflow-execution/workflows/${workflowId}/keywords`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch workflow keywords:', error);
    throw error;
  }
};

export const updateWorkflowKeywords = async (workflowId: string, keywords: string[]): Promise<APIResponse<void>> => {
  try {
    const response = await api.put(`/workflow-execution/workflows/${workflowId}/keywords`, keywords);
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow keywords:', error);
    throw error;
  }
};

export const addWorkflowKeyword = async (workflowId: string, keyword: {
  keyword: string;
  action: string;
}): Promise<APIResponse<void>> => {
  try {
    const response = await api.post(`/workflow-execution/workflows/${workflowId}/keywords`, keyword);
    return response.data;
  } catch (error) {
    console.error('Failed to add workflow keyword:', error);
    throw error;
  }
};

export const deleteWorkflowKeyword = async (workflowId: string, keyword: string): Promise<void> => {
  try {
    await api.delete(`/workflow-execution/workflows/${workflowId}/keywords/${keyword}`);
  } catch (error) {
    console.error('Failed to delete workflow keyword:', error);
    throw error;
  }
};

// Simulator endpoints
export const simulateWorkflow = async (workflowId: string, data: {
  input: any;
  variables?: Record<string, any>;
}): Promise<APIResponse<any>> => {
  try {
    const response = await api.post(`/workflow-execution/workflows/${workflowId}/simulate`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to simulate workflow:', error);
    throw error;
  }
};

export const getSimulationHistory = async (workflowId: string): Promise<APIResponse<any[]>> => {
  try {
    const response = await api.get(`/workflow-execution/workflows/${workflowId}/simulation-history`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch simulation history:', error);
    throw error;
  }
};

